export namespace DataProvider {
    export interface Component {
        id: number;
        name: string;
        version: string;
        minNumberOfVms: number;
        defaultSizing: DefaultSizing;
        sizing: Sizing[];
    }

    export enum DefaultSizing {
        Fixed = "FIXED",
        Medium = "MEDIUM",
        Small = "SMALL",
    }

    export interface Sizing {
        id: string;
        vcpu?: Memory;
        memory: Memory;
        storage: Memory;
        vpu?: Memory;
        "storage-large"?: Memory;
        "storage-x-large"?: Memory;
    }

    export interface Memory {
        unit: Unit;
        value: string;
    }

    export enum Unit {
        Core = "CORE",
        GB = "GB",
        GHz = "GHz",
        KB = "KB",
        TB = "TB",
        TiB = "TiB",
        PCent = "%"
    }

    export interface ValueUnit{
        unit: string | null;
        value: string;
        min?: number;
        max?: number;
    }

    export interface NameCode{
        name: string;
        code: string;
    }

    export interface Configuration {
        id:       string;
        dataType: DataType;
        limits?:  Limits;
        default:  Default;
        options?: Default[];
    }

    export interface InputConfigurationResponse {
        configurations: Configuration[];
        queries: Queries;
    }

    export enum DataType {
        Options = "Options",
        Scalar = "Scalar",
    }

    export interface Default {
        value: string;
        unit:  Unit | null;
        disabled?: boolean;
    }

    export interface Limits {
        min: Default;
        max: Default;
    }

    export interface Queries {
        storagetype:     string;
        vsanarchitecture:string;
        profilemodecode: string;
        workloadoption:  string;
        deploymentcode:  string;
        ioaccesspattern: string;
        strategies:      string;
        hostfailurestotolerate: number;
        ioratio:         string;
        iosize:          number;
        raid:            string;
    }

    export interface DefaultInputConfig {
        storageConfiguration: StorageConfiguration;
        hardwareConfiguration: HardwareConfiguration;
        clusterConfiguration: ClusterConfiguration;
        workload: Workload;
        workloadDetails: WorkloadDetails;
        advancedSettings: AdvancedSettings
        /** dHCI (vSAN Max) limits/defaults from the clusterType=D_HCI Configuration API call; consumed for role groups and workload resilience options. */
        dhci?: DhciInputOverlay;
    }

    /** Subset of the D_HCI configurations response that overrides the HCI-derived limits/defaults. */
    export interface DhciInputOverlay {
        hostFailuresToTolerate?: DefaultOptions2;
        faultToleranceMethod?: DefaultOptions2;
        networkSpeed?: ValueUnit;
        networkSpeedEW?: ValueUnit;
        /** BE sends a fixed option list; min/max are derived from it, value is the BE default. */
        networkSpeedNS?: ValueUnit;
        computeGrowthFactor?: ValueUnit;
        storageGrowthFactor?: ValueUnit;
    }

    export interface StorageConfiguration {
        storageType: DefaultOptions;
        vsanArchitecture: DefaultOptions;
        vsanProfileMode?: DefaultOptions;
        dataEncryption?: Default;
    }
    export interface HardwareConfiguration {
        cpuVendor: string;
        cpuGeneration: string;
        cpuSeries: string;
        cpuCategory: string;
        cpuModel: string;
        cores: ValueUnit;
        clockSpeed: ValueUnit;
        sockets: DefaultOptions2;
        numberOfDrives: ValueUnit;
        driveSize: DefaultOptions2;
        customDriveSize?: ValueUnit;
        serverRam: ValueUnit;
        networkSpeed: ValueUnit;
        cacheTierMediaRating?: DefaultOptions2;
        cacheDriveSlots?: ValueUnit;
        diskGroup?: DefaultOptions2;
        userDefinedDiskGroups?: ValueUnit;
    }
    export interface ClusterConfiguration {
        clusterType?: DefaultOptions2;
        operationsReserve: DefaultOptions2;
        hostRebuildReserve: DefaultOptions2;
        accountForSwapSpace: DefaultOptions2;
        growthFactor: ValueUnit;
        storageEfficiencyPreference?: DefaultOptions2;
        cpuHeadroomSteadyState: ValueUnit;
        cpuHeadroomHostFailureScenario: ValueUnit;
        hostFailuresToTolerate?: DefaultOptions2;
        faultToleranceMethod?: DefaultOptions2;
        autoMode?: Default;
        vcpuPerCore?: ValueUnit;
        vMemPerRam?: ValueUnit;
    }

    export interface Workload {
        workload: DefaultOptions;
        workloadType: DefaultOptions;
    }
    export interface WorkloadDetails {
        totalCountOfVms: ValueUnit;
        storagePerVM: ValueUnit;
        vcpuPerCore: ValueUnit;
        vcpuPerVM: ValueUnit;
        vmemPerVM: ValueUnit;
        iopsPerVM: ValueUnit;
        aggregateIops?: ValueUnit;
        hostFailuresToTolerate: DefaultOptions2;
        faultToleranceMethod: DefaultOptions2;
        dedup?: ValueUnit;
        compression?: ValueUnit;
        ioAccessPattern: DefaultOptions2;
        ioSize: DefaultOptions2;
        ioRatio: DefaultOptions2;
        sizeOfMasterVM?: ValueUnit;
        numOfDisksInMasterVM?: ValueUnit;
        numOfLinkedClonePools?: ValueUnit;
        expecVmdkGrowth?: ValueUnit;
        persistentDiskSize?: ValueUnit;
        sysDisposableDiskSize?: ValueUnit;
        expecCloneGrowth?: ValueUnit;
        numOfInstClonePools?: ValueUnit;
        autoMode?: Default;
    }
    export interface AdvancedSettings {
        resourceUtilizationPlan: ResourceUtilizationPlan;
        vSANOverheads: VSANOverheads;
    }
    export interface VSANOverheads {
        coresConsumed: ValueUnit;
        vSANIOAmplifier?: ValueUnit;
        maxIOpsPerDisk?: ValueUnit;
        maxBandwidthPerDisk?: ValueUnit;
        cacheWriteIOAmplifier?: ValueUnit;
        capacityWriteIOAmplifier?: ValueUnit;
        coresConsumedPerMBPS?: ValueUnit;
        coresConsumedPerIOPS?: ValueUnit;
    }
    export interface ResourceUtilizationPlan {
        avgMemoryConsumption: ValueUnit;
        avgConsumption: ValueUnit;
    }

    export interface DefaultOptions {
        default: ValueUnit;
        options: NameCode[];
    }

    export interface DefaultOptions2 {
        default: ValueUnit;
        options: ValueUnit[];
    }

}
